import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import SeoHead from '../components/SeoHead';
import { localizedPath } from '../config/seo';
import './TresImmo.css';

const BaufortschrittPage = () => {
  const { lang, setLang } = useLanguage();

  const content = {
    de: {
      title: 'Baufortschritt',
      subtitle: 'Projekt Am Waldrand – Oberbozen',
      backLink: 'Zurück zur Startseite',
      intro: 'Verfolgen Sie die Entstehung unseres exklusiven Wohnbauprojekts am Waldrand in Oberbozen. Wir dokumentieren jeden Meilenstein für Sie.',
      photoAlt: 'Baustelle Am Waldrand Oberbozen – Decke über dem 1. Obergeschoss am 8. August 2026',
      photoDate: '08. August 2026',
      phases: [
        {
          date: 'Q4 2025',
          title: 'Planung & Genehmigungen',
          text: 'Abschluss der architektonischen Planung, Einholung aller behördlichen Genehmigungen und Baubewilligungen. Das Grundstück am Waldrand wurde gesichert.',
          status: 'done',
        },
        {
          date: 'Q1 2026',
          title: 'Erdarbeiten & Aushub',
          text: 'Aushubarbeiten, Errichtung des Fundaments und der Tiefgarage. Die Bodenplatte bildet das solide Fundament für das gesamte Gebäude.',
          status: 'done',
        },
        {
          date: 'Q2 – Q3 2026',
          title: 'Rohbau',
          text: 'Errichtung der tragenden Wände, Decken, Innenwände und Dachstuhl. Stand 8. August 2026: Die Decke über dem 1. Obergeschoss ist hergestellt; der Rohbau wird abschnittsweise weitergeführt.',
          status: 'active',
        },
        {
          date: 'Q4 2026 – Q1 2027',
          title: 'Ausbau',
          text: 'Installation der Haustechnik (Heizung, Sanitär, Elektrik), Estricharbeiten, Trockenbau und Malerarbeiten. KlimaHaus-Dämmung wird fertiggestellt.',
          status: 'upcoming',
        },
        {
          date: 'Q2 – Q3 2027',
          title: 'Baufertigstellung',
          text: 'Feinarbeiten, Bodenbeläge, Küchen, Badezimmer-Ausstattung. Terrassen und Gärten werden angelegt. Finale Qualitätskontrolle.',
          status: 'upcoming',
        },
        {
          date: 'Q3 – Q4 2027',
          title: 'Übergabe',
          text: 'Schlüsselübergabe an die neuen Eigentümer. Einzug in Ihr neues Zuhause am Waldrand.',
          status: 'upcoming',
        },
      ],
      statusLabels: { done: 'Abgeschlossen', active: 'In Arbeit', upcoming: 'Geplant' },
      ctaUnits: 'Zu den Einheiten',
      ctaContact: 'Kontakt aufnehmen',
    },
    it: {
      title: 'Avanzamento Lavori',
      subtitle: 'Progetto Ai Margini del Bosco – Soprabolzano',
      backLink: 'Torna alla pagina principale',
      intro: 'Seguite la nascita del nostro esclusivo progetto residenziale ai margini del bosco a Soprabolzano. Documentiamo ogni traguardo per voi.',
      photoAlt: 'Cantiere Ai Margini del Bosco Soprabolzano – solaio sopra il primo piano, 8 agosto 2026',
      photoDate: '8 agosto 2026',
      phases: [
        { date: 'Q4 2025', title: 'Progettazione & Permessi', text: 'Completamento della progettazione architettonica, ottenimento di tutti i permessi edilizi.', status: 'done' },
        { date: 'Q1 2026', title: 'Scavo & Sterro', text: 'Lavori di scavo, costruzione delle fondamenta e del garage sotterraneo.', status: 'done' },
        { date: 'Q2 – Q3 2026', title: 'Struttura Grezza', text: 'Costruzione dei muri portanti, solai, pareti interne e struttura del tetto. Stato all’8 agosto 2026: il solaio sopra il primo piano è stato realizzato.', status: 'active' },
        { date: 'Q4 2026 – Q1 2027', title: 'Lavori Interni', text: 'Installazione impiantistica, massetto, cartongesso e tinteggiatura. Completamento isolamento KlimaHaus.', status: 'upcoming' },
        { date: 'Q2 – Q3 2027', title: 'Completamento Lavori', text: 'Finiture, pavimenti, cucine, bagni. Realizzazione terrazze e giardini. Controllo qualità finale.', status: 'upcoming' },
        { date: 'Q3 – Q4 2027', title: 'Consegna', text: 'Consegna chiavi ai nuovi proprietari. Trasloco nella vostra nuova casa ai margini del bosco.', status: 'upcoming' },
      ],
      statusLabels: { done: 'Completato', active: 'In Corso', upcoming: 'Pianificato' },
      ctaUnits: 'Alle unità',
      ctaContact: 'Contattaci',
    },
    en: {
      title: 'Construction Progress',
      subtitle: "Project At the Forest's Edge – Oberbozen",
      backLink: 'Back to homepage',
      intro: "Follow the creation of our exclusive residential project at the forest's edge in Oberbozen. We document every milestone for you.",
      photoAlt: "Construction site At the Forest's Edge Oberbozen – slab above the first floor, 8 August 2026",
      photoDate: '8 August 2026',
      phases: [
        { date: 'Q4 2025', title: 'Planning & Permits', text: 'Completion of architectural planning, obtaining all building permits and approvals.', status: 'done' },
        { date: 'Q1 2026', title: 'Excavation & Earthworks', text: 'Excavation work, construction of the foundation and underground garage.', status: 'done' },
        { date: 'Q2 – Q3 2026', title: 'Shell Construction', text: 'Construction of load-bearing walls, slabs, interior walls and roof structure. As of 8 August 2026, the slab above the first floor has been completed.', status: 'active' },
        { date: 'Q4 2026 – Q1 2027', title: 'Interior Fit-Out', text: 'Installation of building services, screed, drywall and painting. KlimaHaus insulation completed.', status: 'upcoming' },
        { date: 'Q2 – Q3 2027', title: 'Construction Completion', text: 'Finishing works, flooring, kitchens, bathrooms. Terraces and gardens landscaped. Final quality inspection.', status: 'upcoming' },
        { date: 'Q3 – Q4 2027', title: 'Handover', text: "Key handover to new owners. Move into your new home at the forest's edge.", status: 'upcoming' },
      ],
      statusLabels: { done: 'Completed', active: 'In Progress', upcoming: 'Planned' },
      ctaUnits: 'View units',
      ctaContact: 'Get in touch',
    },
  };

  const c = content[lang] || content.de;

  return (
    <>
    <SeoHead page="baufortschritt" />
    <div className="tres-page">
      <div className="bg-fixed" /><div className="ring" /><div className="ring" /><div className="ring" />
      <nav className="topnav">
        <a href={localizedPath(lang, '/')} className="topnav-logo" style={{ textDecoration: 'none' }}>TRES IMMO</a>
        <div />
        <div className="topnav-right">
          <div className="lang-switcher">
            {['de', 'it', 'en'].map((l, i) => (
              <React.Fragment key={l}>
                {i > 0 && <span>·</span>}
                <button className={lang === l ? 'active' : ''} onClick={() => setLang(l)}>{l.toUpperCase()}</button>
              </React.Fragment>
            ))}
          </div>
        </div>
      </nav>

      <section style={{ padding: 'clamp(6rem,12vw,10rem) clamp(1.5rem,6vw,5rem) 4rem', maxWidth: 900, margin: '0 auto', position: 'relative', zIndex: 2 }}>
        <a href={localizedPath(lang, '/')} style={{ fontSize: '0.65rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: 'var(--gold)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.4rem', marginBottom: '2rem' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
          {c.backLink}
        </a>
        <p className="section-label visible">{c.subtitle}</p>
        <h1 className="projekt-headline visible" style={{ marginBottom: '1.5rem' }}><em>{c.title}</em></h1>
        <p style={{ fontSize: '0.88rem', lineHeight: 1.8, color: 'var(--muted)', fontWeight: 200, marginBottom: '3rem', maxWidth: 680 }}>{c.intro}</p>

        {/* Aktuelles Baufoto */}
        <div style={{ marginBottom: '3.5rem', borderRadius: '4px', overflow: 'hidden', position: 'relative' }}>
          <img
            src="/img/baufortschritt-aktuell-august-2026.png"
            alt={c.photoAlt}
            style={{ width: '100%', maxHeight: '480px', objectFit: 'cover', display: 'block' }}
          />
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
            padding: '1.5rem 1.2rem 0.9rem',
            display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end'
          }}>
            <span style={{ fontSize: '0.7rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.9)' }}>
              Oberbozen · Baustelle
            </span>
            <span style={{ fontSize: '0.65rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--gold)' }}>
              {c.photoDate}
            </span>
          </div>
        </div>

        {/* Timeline */}
        <div className="bf-timeline" data-testid="construction-timeline">
          {c.phases.map((phase, i) => (
            <div key={i} className={`bf-phase bf-phase--${phase.status}`} data-testid={`phase-${i}`}>
              <div className="bf-phase-marker">
                <div className="bf-phase-dot" />
                {i < c.phases.length - 1 && <div className="bf-phase-line" />}
              </div>
              <div className="bf-phase-content">
                <div className="bf-phase-meta">
                  <span className="bf-phase-date">{phase.date}</span>
                  <span className={`bf-phase-status bf-phase-status--${phase.status}`}>
                    {c.statusLabels[phase.status]}
                  </span>
                </div>
                <h3 className="bf-phase-title">{phase.title}</h3>
                <p className="bf-phase-text">{phase.text}</p>
              </div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: '3rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <a className="wa-btn visible" href={`${localizedPath(lang, '/')}#einheiten`} style={{ textDecoration: 'none' }}>
            <span>{c.ctaUnits}</span>
          </a>
          <a className="wa-btn visible" href="https://wa.me/393935652804" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
            <span>{c.ctaContact}</span>
          </a>
        </div>
      </section>

      <footer className="tres-footer">
        <div className="footer-bottom">&copy; {new Date().getFullYear()} TRES IMMO &middot; Oberbozen, Ritten, Südtirol</div>
      </footer>
    </div>
    </>
  );
};

export default BaufortschrittPage;
